/**
 * App Configuration - Environment-aware URLs for Cross-App Navigation
 * 
 * In development: Uses localhost URLs
 * In production: Uses Firebase Hosting URLs
 * 
 * Environment Variables:
 * - VITE_PFMEA_APP_URL: URL of the PFMEA app (this app)
 * - VITE_DFMEA_APP_URL: URL of the DFMEA (Magnus Core) app
 */

// Detect if we're in production mode
const isProduction = import.meta.env.PROD;

// Production URLs (Firebase Hosting)
const PRODUCTION_DFMEA_URL = 'https://dfr-living-reliability-system.web.app';
const PRODUCTION_PFMEA_URL = 'https://magnus-pfmea.web.app';  // Update this with your actual PFMEA Firebase URL

// Development URLs (localhost)
const DEV_DFMEA_URL = 'http://localhost:5173';
const DEV_PFMEA_URL = 'http://localhost:5174';

/**
 * Get the URL for the PFMEA App (this app)
 */
export const getPfmeaAppUrl = (): string => {
    // First check if explicitly set via environment variable
    if (import.meta.env.VITE_PFMEA_APP_URL) {
        return import.meta.env.VITE_PFMEA_APP_URL;
    }

    // Otherwise use production/dev defaults
    return isProduction ? PRODUCTION_PFMEA_URL : DEV_PFMEA_URL;
};

/**
 * Get the URL for the DFMEA (Magnus Core) App
 */
export const getDfmeaAppUrl = (): string => {
    // First check if explicitly set via environment variable
    if (import.meta.env.VITE_DFMEA_APP_URL) {
        return import.meta.env.VITE_DFMEA_APP_URL;
    }

    // Otherwise use production/dev defaults
    return isProduction ? PRODUCTION_DFMEA_URL : DEV_DFMEA_URL;
};

/**
 * Get the URL for the Resolve App
 */
const PRODUCTION_RESOLVE_URL = 'https://magnus-resolve.web.app';
const DEV_RESOLVE_URL = 'http://localhost:5175';

export const getResolveAppUrl = (): string => {
    if (import.meta.env.VITE_RESOLVE_APP_URL) {
        return import.meta.env.VITE_RESOLVE_APP_URL;
    }
    return isProduction ? PRODUCTION_RESOLVE_URL : DEV_RESOLVE_URL;
};

/**
 * Build a cross-app navigation URL
 */
export const buildCrossAppUrl = (baseUrl: string, path: string): string => {
    // Ensure no double slashes
    const cleanBase = baseUrl.replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${cleanBase}${cleanPath}`;
};

// Export for convenience
export const appConfig = {
    pfmeaUrl: getPfmeaAppUrl(),
    dfmeaUrl: getDfmeaAppUrl(),
    isProduction,
};

export default appConfig;
